<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import RowActions from "@/components-ng/RowActions/RowActions.vue";
import RowActionsButton from "@/components-ng/RowActions/RowActionsButton.vue";
import { ArtHandler } from "@/handlers/artHandler";
import { SolutionTrainHandler } from "@/handlers/solutionTrainHandler";
import { TeamHandler } from "@/handlers/teamHandler";
import { useAppStore } from "@/stores";
import { displayUserName } from "@/utils/userName";

import DataTableV2 from "../DataTableV2.vue";

const props = defineProps({
  // The selected org entity: { type: "team" | "art" | "train", id, name }, or
  // null while nothing is selected
  entity: { type: Object, default: null },
});

const artHandler = new ArtHandler();
const solutionTrainHandler = new SolutionTrainHandler();
const teamHandler = new TeamHandler();

const route = useRoute();
const router = useRouter();
const store = useAppStore();

const canEditUsers = computed(() => store.isAdmin && !store.isTraining);

// Only teams hold plain members, an ART or solution train is run by its admins
const ENTITY = {
  team: {
    label: "Team",
    memberType: "members",
    adminLabel: "Team admin",
    route: "editTeam",
    canEdit: (id) => store.canEditTeam(id),
    members: (id) => teamHandler.getTeamMembers(id),
  },
  art: {
    label: "ART",
    memberType: "admins",
    adminLabel: "ART admin",
    route: "editArt",
    canEdit: (id) => store.canEditArt(id),
    members: (id) => artHandler.getMembers(id),
  },
  train: {
    label: "Solution Train",
    memberType: "admins",
    adminLabel: "Solution train admin",
    route: "editSolutionTrain",
    canEdit: (id) => store.canEditTrain(id),
    members: (id) => solutionTrainHandler.getMembers(id),
  },
};

const kind = computed(() => ENTITY[props.entity?.type]);
const adminLabel = computed(() => kind.value?.adminLabel);
const subtitle = computed(() => kind.value && `${kind.value.label} ${kind.value.memberType}`);
const emptyMessage = computed(() => `No ${kind.value?.memberType}`);
// `from` carries this page's url, which holds the tree's selection and what is
// open, so saving over there brings you back to the tree exactly as you left it
const entityLink = computed(() =>
  kind.value?.canEdit(props.entity.id)
    ? {
        name: kind.value.route,
        params: { id: `${props.entity.id}` },
        query: { from: route.fullPath },
      }
    : null,
);

const members = ref([]);
const loading = ref(false);

// An ART or solution train is run by its admins, so only those are shown for it.
// The endpoints report every member, assigning a non-admin is just not offered.
function membersToShow(type, list) {
  return ENTITY[type].memberType === "admins" ? list.filter((member) => member.is_admin) : list;
}

// Monotonic guard so a slow earlier request can't overwrite a newer selection.
let seq = 0;

watch(
  () => props.entity,
  async (entity) => {
    const mine = ++seq;
    members.value = [];
    if (!entity) return;
    loading.value = true;
    try {
      const res = await ENTITY[entity.type].members(entity.id);
      if (mine === seq) {
        members.value = membersToShow(entity.type, res.data ?? []);
      }
    } finally {
      if (mine === seq) {
        loading.value = false;
      }
    }
  },
  { immediate: true },
);

// Content floors, not totals: the cell adds its padding on top
const columns = computed(() => [
  {
    id: "name",
    // A member added by email alone has no name stored - the email's local part
    // stands in for it, as it does in the users list
    accessorFn: displayUserName,
    header: "Name",
    meta: { style: { minWidth: "150px", whiteSpace: "nowrap" } },
  },
  {
    accessorKey: "email",
    header: "Email",
    meta: { style: { minWidth: "200px", whiteSpace: "nowrap" } },
  },
  {
    id: "org_role",
    accessorFn: (member) => (member.is_admin ? adminLabel.value : "Member"),
    header: "Organization role",
    meta: { style: { minWidth: "140px", whiteSpace: "nowrap" } },
  },
  // No padding: it holds nothing, and 40px of it is what tips a long email past
  // the width the PI Details panel has to give the table.
  { id: "spacer", header: "", meta: { style: { width: "100%", padding: 0 } } },
  ...(canEditUsers.value
    ? [{ id: "action", header: "", meta: { style: { minWidth: "32px" } } }]
    : []),
]);

function goEditUser(member) {
  // Same `from` as the entity links: saving the user returns to this tree
  router.push({
    name: "editUser",
    params: { id: member.id },
    query: { from: route.fullPath },
  });
}
</script>

<template>
  <section
    class="org-entity-members"
    :aria-label="kind?.memberType === 'admins' ? 'Admins' : 'Members'"
  >
    <header class="panel-header">
      <h2 class="panel-title">
        <!-- The name is the way to the entity's own page, as it is in the lists.
             A pen next to a name reads as "rename this" instead. -->
        <router-link v-if="entityLink" class="entity-link" :to="entityLink">
          {{ entity.name }}
        </router-link>
        <template v-else>{{ entity?.name ?? "Members" }}</template>
      </h2>
      <!-- Left to the consumer: this component knows only the org -->
      <div v-if="entity && $slots.actions" class="panel-header-actions">
        <slot name="actions" :entity />
      </div>
    </header>
    <div class="panel-body">
      <p v-if="!entity" class="hint">
        Select a team to see its members, or an ART or solution train to see its admins.
      </p>
      <template v-else>
        <p class="members-subtitle">{{ subtitle }}</p>
        <DataTableV2 :data="members" :columns :loading :empty-message>
          <template #cell-name="{ row }">
            <span class="name-value" :title="displayUserName(row)">{{ displayUserName(row) }}</span>
          </template>
          <template #cell-email="{ row }">
            <span class="email-value" :title="row.email">{{ row.email }}</span>
          </template>
          <template #cell-org_role="{ row }">
            <span v-if="row.is_admin" class="admin-role">{{ adminLabel }}</span>
            <span v-else class="member-role">Member</span>
          </template>
          <template #cell-action="{ row }">
            <RowActions>
              <RowActionsButton
                icon="general/edit"
                tooltip="Edit"
                aria-label="Edit"
                @click="goEditUser(row)"
              />
            </RowActions>
          </template>
        </DataTableV2>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/layout";
@use "@/styles/mixins/typography";

.org-entity-members {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 36px;
  padding: 12px layout.$content-pad;
  border-bottom: 1px solid c.$border-light;
}

.panel-title {
  @include typography.medium("semi-bold");

  margin: 0;
  overflow: hidden;
  color: c.$text;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-inline-start: auto;
  flex: none;
}

.entity-link {
  color: inherit;
  text-decoration: none;

  &:hover {
    color: c.$main;
    text-decoration: underline;
  }
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  // No top padding: it sits inside the scrollport, above the sticky header, so
  // rows scroll through it. The subtitle's margin gives the gap as content.
  padding: 0 layout.$content-pad 16px;

  --table-header-top: 0px;
}

.panel-body :deep(.table) {
  th,
  td {
    padding-inline: var(--members-cell-pad, 1.25rem);
  }
}

.name-value,
.email-value {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name-value {
  max-width: var(--members-name-max, none);
}

.email-value {
  max-width: var(--members-email-max, none);
}

.members-subtitle {
  @include typography.small("semi-bold");

  margin: 8px 0 12px;
  color: c.$text-subtle;
}

.admin-role {
  @include typography.medium("bold");

  color: c.$text;
}

.member-role {
  color: c.$text-subtle;
}

:deep(.row-actions) {
  opacity: 0;
  transition: opacity 100ms ease;
}

:deep(tr:hover .row-actions),
:deep(tr:focus-within .row-actions) {
  opacity: 1;
}

.hint {
  @include typography.small;

  margin: 0;
  padding: 8px 0;
  color: c.$text-subtle;
}
</style>
